import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import {
  X,
  Lightning,
  Brain,
  Fire,
  ShieldCheck,
  Lock,
  CheckCircle,
  Clock,
  ArrowRight,
  Target,
  Trophy,
  SpinnerGap,
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaywallContext = createContext(null);
export const usePaywall = () => useContext(PaywallContext);

const TRIGGER_CONFIG = {
  first_session_gate: {
    icon: Lightning,
    iconColor: 'text-accent',
    headline: "You're off to a great start",
  },
  feature_lock: {
    icon: Lock,
    iconColor: 'text-accent',
    headline: 'Unlock this feature to keep going',
  },
  struggle: {
    icon: Brain,
    iconColor: 'text-purple-400',
    headline: "Let's turn those mistakes into lessons",
  },
  success: {
    icon: Trophy,
    iconColor: 'text-success',
    headline: "You're doing great!",
  },
  exit_intent: {
    icon: Clock,
    iconColor: 'text-amber-500',
    headline: 'Before you go...',
    badge: 'LAST CHANCE',
  },
};

function PaywallModal({ config, onClose }) {
  const navigate = useNavigate();
  const { pricing } = useFeatures();
  const [exiting, setExiting] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  if (!config) return null;

  const {
    trigger = 'first_session_gate',
    message = '',
    recommended_plan = 'standard',
    offer = 'standard',
    discount_eligible = false,
  } = config;

  const variant = TRIGGER_CONFIG[trigger] || TRIGGER_CONFIG.first_session_gate;
  const Icon = variant.icon;

  const corePrice = pricing.standard || 4.99;
  const premiumPrice = pricing.premium || 9.99;
  const oneTimePrice = pricing.one_time || 7.99;

  const isDiscount = offer === 'discount';
  const discountPercent = isDiscount ? 50 : 0;
  const displayCorePrice = isDiscount ? (corePrice / 2).toFixed(2) : corePrice;
  const displayPremiumPrice = isDiscount ? (premiumPrice / 2).toFixed(2) : premiumPrice;

  const handleCheckout = async (tier) => {
    setCheckingOut(tier);
    try {
      await axios.post(`${API}/paywall/event`, {
        trigger, action: 'checkout', plan_shown: tier,
      }, { withCredentials: true });
    } catch {}

    try {
      const { data } = await axios.post(`${API}/subscription/checkout`, {
        tier,
        origin_url: window.location.origin,
        discount_percent: discountPercent,
      }, { withCredentials: true });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setCheckingOut(null);
      onClose();
      navigate('/subscription');
    }
  };

  const handleDismiss = async () => {
    try {
      await axios.post(`${API}/paywall/event`, {
        trigger, action: 'dismiss', plan_shown: recommended_plan,
      }, { withCredentials: true });
    } catch {}
    setExiting(true);
    setTimeout(() => onClose(), 200);
  };

  const handleExitIntentCheck = () => {
    if (discount_eligible && trigger !== 'exit_intent') {
      onClose();
      setTimeout(() => {
        window.__paywallShowExitIntent?.();
      }, 300);
      return;
    }
    handleDismiss();
  };

  const showPremium = recommended_plan === 'premium' || trigger === 'struggle';
  const showOneTime = offer === 'one_time' || trigger === 'exit_intent';

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        'bg-background/80 backdrop-blur-sm',
        exiting ? 'animate-out fade-out' : 'animate-in fade-in'
      )}
      onClick={handleExitIntentCheck}
      data-testid="paywall-modal"
    >
      <div
        className={cn(
          'bg-card border border-border rounded-sm shadow-2xl w-full max-w-md overflow-hidden',
          exiting ? 'animate-out zoom-out-95' : 'animate-in zoom-in-95'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4">
          <button
            onClick={handleExitIntentCheck}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="paywall-close"
          >
            <X weight="bold" className="w-5 h-5" />
          </button>

          {variant.badge && (
            <span className="inline-block text-[10px] font-black uppercase tracking-widest text-amber-500 bg-amber-500/10 px-2 py-1 rounded-sm mb-3">
              {variant.badge}
            </span>
          )}

          {isDiscount && !variant.badge && (
            <span
              className="inline-block text-[10px] font-black uppercase tracking-widest text-success bg-success/10 px-2 py-1 rounded-sm mb-3"
              data-testid="discount-badge"
            >
              50% OFF FIRST MONTH
            </span>
          )}

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
              <Icon weight="fill" className={cn('w-6 h-6', variant.iconColor)} />
            </div>
            <h2 className="font-heading font-black text-xl leading-tight">{variant.headline}</h2>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
        </div>

        {/* Plan options */}
        <div className="px-6 pb-4 space-y-2">
          {!showOneTime && (
            <button
              onClick={() => handleCheckout('standard')}
              disabled={!!checkingOut}
              className={cn(
                'w-full text-left border rounded-sm p-3 transition-all',
                !showPremium ? 'border-accent/40 bg-accent/5' : 'border-border hover:border-accent/30'
              )}
              data-testid="paywall-plan-core"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-sm">Core Plan</p>
                  <p className="text-xs text-muted-foreground">Full question bank, mock exams, smart review</p>
                </div>
                <div className="text-right flex items-center gap-2">
                  {checkingOut === 'standard' ? (
                    <SpinnerGap className="w-5 h-5 animate-spin text-accent" />
                  ) : (
                    <>
                      {isDiscount && (
                        <span className="text-xs text-muted-foreground line-through">£{corePrice}</span>
                      )}
                      <span className="font-black text-lg">£{displayCorePrice}</span>
                      <span className="text-xs text-muted-foreground">/mo</span>
                    </>
                  )}
                </div>
              </div>
            </button>
          )}

          {!showOneTime && (
            <button
              onClick={() => handleCheckout('premium')}
              disabled={!!checkingOut}
              className={cn(
                'w-full text-left border rounded-sm p-3 transition-all relative overflow-hidden',
                showPremium ? 'border-accent/40 bg-accent/5' : 'border-border hover:border-accent/30'
              )}
              data-testid="paywall-plan-premium"
            >
              {showPremium && (
                <span className="absolute top-0 right-0 text-[8px] font-black uppercase bg-accent text-accent-foreground px-2 py-0.5">
                  RECOMMENDED
                </span>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <Fire weight="fill" className="w-4 h-4 text-accent" />
                    <p className="font-bold text-sm">Pass Faster</p>
                  </div>
                  <p className="text-xs text-muted-foreground">Everything + AI explanations & hazard perception</p>
                </div>
                <div className="text-right flex items-center gap-2">
                  {checkingOut === 'premium' ? (
                    <SpinnerGap className="w-5 h-5 animate-spin text-accent" />
                  ) : (
                    <>
                      {isDiscount && (
                        <span className="text-xs text-muted-foreground line-through">£{premiumPrice}</span>
                      )}
                      <span className="font-black text-lg">£{displayPremiumPrice}</span>
                      <span className="text-xs text-muted-foreground">/mo</span>
                    </>
                  )}
                </div>
              </div>
            </button>
          )}

          {showOneTime && (
            <button
              onClick={() => handleCheckout('one_time')}
              disabled={!!checkingOut}
              className="w-full text-left border-2 border-amber-500/40 bg-amber-500/5 rounded-sm p-4 transition-all"
              data-testid="paywall-plan-one-time"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <Clock weight="fill" className="w-4 h-4 text-amber-500" />
                    <p className="font-bold text-sm">30-Day Full Access</p>
                  </div>
                  <p className="text-xs text-muted-foreground">No subscription. Pass and move on.</p>
                </div>
                <div className="text-right flex items-center gap-2">
                  {checkingOut === 'one_time' ? (
                    <SpinnerGap className="w-5 h-5 animate-spin text-amber-500" />
                  ) : (
                    <>
                      <span className="font-black text-xl">£{oneTimePrice}</span>
                      <span className="text-xs text-muted-foreground">one-time</span>
                    </>
                  )}
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Compare all plans link */}
        <div className="px-6 pb-2">
          <button
            onClick={() => { onClose(); navigate('/subscription'); }}
            className="w-full text-center text-xs text-accent hover:text-accent/80 py-1 transition-colors flex items-center justify-center gap-1"
            data-testid="paywall-view-plans"
          >
            Compare all plans <ArrowRight weight="bold" className="w-3 h-3" />
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5">
          <button
            onClick={handleDismiss}
            className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-2 transition-colors"
            data-testid="paywall-skip"
          >
            {trigger === 'exit_intent' ? "No thanks, I'll stick with free" : 'Maybe later'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Nudge banner for dashboard
export function NudgeBanner({ nudge, onAction }) {
  if (!nudge || !nudge.show) return null;

  const bgMap = {
    day1: 'bg-accent/10 border-accent/30',
    day2_3: 'bg-blue-500/10 border-blue-500/30',
    day5_plus: 'bg-amber-500/10 border-amber-500/30',
    expiry_warning: 'bg-destructive/10 border-destructive/30',
    test_date_critical: 'bg-destructive/10 border-destructive/30',
    test_date_urgent: 'bg-amber-500/10 border-amber-500/30',
    test_date_soon: 'bg-blue-500/10 border-blue-500/30',
  };
  const iconMap = {
    day1: Target,
    day2_3: ShieldCheck,
    day5_plus: Lightning,
    expiry_warning: Clock,
    test_date_critical: Clock,
    test_date_urgent: Clock,
    test_date_soon: Target,
  };
  const Icon = iconMap[nudge.type] || Target;

  return (
    <div
      className={cn('border rounded-sm p-4 flex items-center gap-3', bgMap[nudge.type] || 'bg-muted border-border')}
      data-testid="nudge-banner"
    >
      <Icon weight="fill" className="w-5 h-5 flex-shrink-0" />
      <p className="text-sm flex-1">{nudge.message}</p>
      {nudge.plan ? (
        <Button size="sm" onClick={onAction} className="rounded-sm text-xs flex-shrink-0" data-testid="nudge-cta">
          {nudge.cta} <ArrowRight weight="bold" className="w-3 h-3 ml-1" />
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={onAction} className="rounded-sm text-xs flex-shrink-0" data-testid="nudge-cta">
          {nudge.cta} <ArrowRight weight="bold" className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
}


// Test readiness progress bar with milestones and breakdown
export function ReadinessBar({ readiness, isLocked, lockThreshold, milestone, breakdown }) {
  const milestones = [
    { pct: 15, label: 'Getting started' },
    { pct: 30, label: 'Building knowledge' },
    { pct: lockThreshold || 40, label: 'Free limit' },
    { pct: 60, label: 'Strong foundation' },
    { pct: 80, label: 'Almost ready' },
    { pct: 100, label: 'Test ready!' },
  ];

  // Determine bar color based on state
  const barColor = isLocked
    ? 'bg-destructive/80'
    : readiness >= 80
    ? 'bg-success'
    : readiness >= 40
    ? 'bg-accent'
    : 'bg-primary';

  return (
    <div data-testid="readiness-bar">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Test Readiness
        </span>
        <span className={cn('text-sm font-black', isLocked ? 'text-destructive' : readiness >= 80 ? 'text-success' : 'text-accent')}>
          {readiness}%
        </span>
      </div>

      {/* Progress bar with milestone markers */}
      <div className="relative h-4 bg-muted rounded-sm overflow-hidden">
        <div
          className={cn('h-full transition-all duration-1000 ease-out rounded-sm', barColor)}
          style={{ width: `${isLocked ? lockThreshold : readiness}%` }}
        />
        {/* Lock wall marker */}
        {lockThreshold && (
          <div
            className="absolute top-0 h-full flex flex-col items-center"
            style={{ left: `${lockThreshold}%`, transform: 'translateX(-50%)' }}
          >
            <div className={cn(
              'w-0.5 h-full',
              isLocked ? 'bg-destructive animate-pulse' : 'bg-border'
            )} />
          </div>
        )}
        {/* Milestone dots */}
        {milestones.filter(m => m.pct > 0 && m.pct < 100 && m.pct !== lockThreshold).map((m) => (
          <div
            key={m.pct}
            className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-background/50"
            style={{ left: `${m.pct}%`, transform: `translate(-50%, -50%)` }}
          />
        ))}
      </div>

      {/* Lock threshold label */}
      {lockThreshold && readiness > 0 && (
        <div className="relative mt-0.5 h-3">
          <span
            className={cn(
              'absolute text-[9px] font-bold uppercase tracking-wider',
              isLocked ? 'text-destructive' : 'text-muted-foreground/50'
            )}
            style={{ left: `${lockThreshold}%`, transform: 'translateX(-50%)' }}
          >
            {isLocked ? 'LOCKED' : 'Free limit'}
          </span>
        </div>
      )}

      {/* Milestone message */}
      {milestone && (
        <div className={cn(
          'mt-2 p-2.5 rounded-sm border text-xs',
          milestone.type === 'locked' ? 'bg-destructive/10 border-destructive/30 text-destructive' :
          milestone.type === 'approaching' ? 'bg-accent/10 border-accent/30 text-accent' :
          'bg-muted border-border text-muted-foreground'
        )} data-testid="readiness-milestone">
          <p className="font-medium leading-relaxed">{milestone.message}</p>
        </div>
      )}

      {/* Breakdown stats (compact) */}
      {breakdown && readiness > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-2" data-testid="readiness-breakdown">
          <div className="text-center">
            <p className="text-xs font-black text-foreground">{breakdown.coverage || 0}%</p>
            <p className="text-[9px] text-muted-foreground">Coverage</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-black text-foreground">{breakdown.accuracy || 0}%</p>
            <p className="text-[9px] text-muted-foreground">Accuracy</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-black text-foreground">{breakdown.categories || 0}/{breakdown.total_categories || 12}</p>
            <p className="text-[9px] text-muted-foreground">Topics</p>
          </div>
          <div className="text-center">
            <p className="text-xs font-black text-foreground">{breakdown.mocks_passed || 0}</p>
            <p className="text-[9px] text-muted-foreground">Mocks</p>
          </div>
        </div>
      )}
    </div>
  );
}


// Provider
export function PaywallProvider({ children }) {
  const { user } = useAuth();
  const { tier } = useFeatures();
  const [modalConfig, setModalConfig] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const lastCheckRef = useRef(0);
  const exitIntentFiredRef = useRef(false);

  useEffect(() => {
    if (user && tier === 'free') {
      fetchPaywallState();
      fetchReadiness();
    }
  }, [user, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  // Exit intent: mouse leaves viewport toward the top
  useEffect(() => {
    if (!user || tier !== 'free') return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitIntentFiredRef.current && !modalConfig) {
        exitIntentFiredRef.current = true;
        showExitIntent();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [user, tier, modalConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaywallState = async () => {
    try {
      const { data } = await axios.get(`${API}/paywall/state`, { withCredentials: true });
      setNudge(data.nudge);
    } catch {}
  };

  const fetchReadiness = async () => {
    try {
      const { data } = await axios.get(`${API}/paywall/progress`, { withCredentials: true });
      setReadiness(data);
    } catch {}
  };

  const checkPaywall = useCallback(async (context = 'general', feature = '') => {
    if (tier !== 'free') return false;

    const now = Date.now();
    if (now - lastCheckRef.current < 2000) return false;
    lastCheckRef.current = now;

    try {
      const { data } = await axios.post(`${API}/paywall/check`, {
        context, feature,
      }, { withCredentials: true });

      if (data.should_show) {
        try {
          await axios.post(`${API}/paywall/event`, {
            trigger: data.trigger, action: 'view', plan_shown: data.recommended_plan,
          }, { withCredentials: true });
        } catch {}
        setModalConfig(data);
        return true;
      }
    } catch {}
    return false;
  }, [tier]);

  const checkFeatureLock = useCallback(async (featureName) => {
    if (tier !== 'free') return false;
    return checkPaywall('feature_lock', featureName);
  }, [tier, checkPaywall]);

  const showExitIntent = useCallback(() => {
    if (tier !== 'free') return;
    setModalConfig({
      trigger: 'exit_intent',
      message: 'Last chance: 30-day full access for £7.99 — no subscription needed',
      recommended_plan: 'one_time',
      offer: 'one_time',
      discount_eligible: true,
    });
  }, [tier]);

  useEffect(() => {
    window.__paywallShowExitIntent = showExitIntent;
    return () => { delete window.__paywallShowExitIntent; };
  }, [showExitIntent]);

  const closeModal = () => setModalConfig(null);

  return (
    <PaywallContext.Provider value={{
      checkPaywall, checkFeatureLock, showExitIntent,
      nudge, readiness, fetchReadiness, fetchPaywallState,
    }}>
      {children}
      {modalConfig && <PaywallModal config={modalConfig} onClose={closeModal} />}
    </PaywallContext.Provider>
  );
}
